import ClockIcon from '@assets/icons/clock.svg';
import i18n from '@/utils/i18n';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DropdownTrigger } from '@usepepper/ui/drafts/dropdown-trigger';
import { formatCurrency } from '@/utils/money';
import { formatDate, momentWithTimezone } from '@/utils/date';
import { GestureResponderEvent, Platform, Pressable, View } from 'react-native';
import { ListItem } from '@usepepper/ui/molecules/list-item';
import { PriceQuoteSummary } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/loaders/use-saved-quotes-loader';
import { SearchableDropdownMenu } from '@usepepper/ui/molecules/searchable-dropdown-menu';
import { Typography } from '@usepepper/ui/atoms/typography';

const DROPDOWN_WIDTH = 360;
// Two text rows + clock row + paddings — used by SearchableDropdownMenu
// to clamp the panel height.
const ITEM_HEIGHT = 82;
// NBSP-flanked bullet keeps the separator glued to its neighbors on
// narrow viewports. Escape sequences keep the non-breaking spaces
// visible in source.
const BULLET_SEPARATOR = '\u00A0\u00A0\u2022\u00A0\u00A0';

interface LoadQuoteDropdownProps {
  onNewQuote: () => void;
  onSelectQuote: (quote: PriceQuoteSummary) => void;
  savedQuotes: PriceQuoteSummary[];
}

type QuoteRow = PriceQuoteSummary & { key: string };

const toQuoteRows = (quotes: PriceQuoteSummary[]): QuoteRow[] =>
  quotes.map((q) => ({ ...q, key: q.priceQuoteId }));

interface NewQuoteOptionProps {
  isFocused: boolean;
  onSelect: () => void;
}

const NewQuoteOption: React.FC<NewQuoteOptionProps> = ({
  isFocused,
  onSelect,
}) => {
  const [styles, theme] = useStyles();

  const handlePress = (e: GestureResponderEvent) => {
    e.stopPropagation();
    onSelect();
  };

  return (
    <Pressable
      accessibilityLabel={i18n.t('sales_portal.price_quotes.new_quote')}
      accessibilityRole="button"
      onPress={handlePress}
      style={({ hovered }) => [
        styles.newQuoteButton,
        (hovered || isFocused) && styles.optionHovered,
      ]}
    >
      <PlusIcon height={14} stroke={theme.sk.color.icon.primary} width={14} />
      <Typography
        color={theme.sk.color.text.primary}
        variant="paragraph-md-600"
      >
        {i18n.t('sales_portal.price_quotes.new_quote')}
      </Typography>
    </Pressable>
  );
};

interface QuoteOptionProps {
  isFocused: boolean;
  item: QuoteRow;
  onSelect: (quote: PriceQuoteSummary) => void;
}

const QuoteOption: React.FC<QuoteOptionProps> = ({
  isFocused,
  item,
  onSelect,
}) => {
  const [styles, theme] = useStyles();

  const handlePress = (e: GestureResponderEvent) => {
    e.stopPropagation();
    onSelect(item);
  };

  const itemCountLabel =
    item.itemCount === 1
      ? i18n.t('sales_portal.price_quotes.item_count_singular', {
          count: item.itemCount,
        })
      : i18n.t('sales_portal.price_quotes.item_count_plural', {
          count: item.itemCount,
        });

  const updatedDateLabel = i18n.t('sales_portal.price_quotes.updated_date', {
    date: formatDate(momentWithTimezone(item.updatedAt)),
  });

  // Compose a screen-reader label that includes the metadata visually
  // present on the row. Without this, VoiceOver only reads the quote
  // name and rows with similar names are indistinguishable.
  const accessibilityLabel = [
    item.name,
    item.accountName,
    itemCountLabel,
    formatCurrency(item.totalAmountInMicros, item.currencyCode),
    updatedDateLabel,
  ].join(', ');

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      onPress={handlePress}
      style={({ hovered }) => [
        styles.quoteRow,
        (hovered || isFocused) && styles.optionHovered,
      ]}
    >
      <ListItem
        bottomElement={
          <View style={styles.updatedRow}>
            <ClockIcon
              height={12}
              stroke={theme.sk.color.icon.tertiary}
              width={12}
            />
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm"
            >
              {updatedDateLabel}
            </Typography>
          </View>
        }
        subtitle={
          <Typography
            color={theme.sk.color.text.secondary}
            numberOfLines={1}
            variant="paragraph-sm"
          >
            {item.accountName}
            {BULLET_SEPARATOR}
            {itemCountLabel}
            {BULLET_SEPARATOR}
            {formatCurrency(item.totalAmountInMicros, item.currencyCode)}
          </Typography>
        }
        title={
          <Typography
            color={theme.sk.color.text.primary}
            numberOfLines={1}
            variant="paragraph-md-600"
          >
            {item.name}
          </Typography>
        }
      />
    </Pressable>
  );
};

const EmptyOption: React.FC = () => {
  const [styles, theme] = useStyles();
  return (
    <View style={styles.emptyContainer}>
      <Typography color={theme.sk.color.text.secondary} variant="paragraph-md">
        {i18n.t('sales_portal.price_quotes.load_quote.empty')}
      </Typography>
    </View>
  );
};

/**
 * Header dropdown listing saved quotes. Selecting a quote fires
 * `onSelectQuote(summary)`. The "+ New Quote" action renders via
 * `SearchableDropdownMenu`'s `headerSlot`; the no-saved-quotes copy
 * renders via `listEmptyComponent`.
 */
export const LoadQuoteDropdown: React.FC<LoadQuoteDropdownProps> = ({
  onNewQuote,
  onSelectQuote,
  savedQuotes,
}) => {
  const [styles, theme] = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const handleClose = useCallback(() => {
    setSearchText('');
    setFocusedIndex(null);
    setIsOpen(false);
  }, []);

  // Web-only: the Dropdown anchor doesn't reposition on viewport resize,
  // so close it instead of letting the panel drift away from the trigger.
  // Native shows an ActionSheet that handles its own dismissal.
  useEffect(() => {
    if (!isOpen || Platform.OS !== 'web') {
      return;
    }
    window.addEventListener('resize', handleClose);
    return () => window.removeEventListener('resize', handleClose);
  }, [handleClose, isOpen]);

  const handleSelectQuote = (quote: PriceQuoteSummary) => {
    onSelectQuote(quote);
    handleClose();
  };

  const handleNewQuote = () => {
    onNewQuote();
    handleClose();
  };

  const listData = useMemo<QuoteRow[]>(() => {
    const allQuotes = toQuoteRows(savedQuotes);
    if (!searchText) {
      return allQuotes;
    }
    const lower = searchText.toLowerCase();
    return allQuotes.filter(
      (q) =>
        q.name.toLowerCase().includes(lower) ||
        q.accountName.toLowerCase().includes(lower),
    );
  }, [savedQuotes, searchText]);

  const trigger = (
    <DropdownTrigger
      isOpen={isOpen}
      onPress={() => setIsOpen(true)}
      style={styles.trigger}
    >
      <Typography
        color={theme.sk.color.text.primary}
        numberOfLines={1}
        variant="paragraph-md"
      >
        {i18n.t('sales_portal.price_quotes.load_quote')}
      </Typography>
    </DropdownTrigger>
  );

  return (
    <SearchableDropdownMenu
      dropdownContainerOverrides={styles.dropdownContainer}
      estimatedListItemHeight={ITEM_HEIGHT}
      focusedIndex={focusedIndex}
      headerSlot={
        <NewQuoteOption isFocused={false} onSelect={handleNewQuote} />
      }
      isVisible={isOpen}
      listData={listData}
      listEmptyComponent={<EmptyOption />}
      onClose={handleClose}
      onFocusedIndexChange={setFocusedIndex}
      onSearch={setSearchText}
      placeholderText={i18n.t(
        'sales_portal.price_quotes.load_quote.search_placeholder',
      )}
      position="bottom-end"
      renderItem={(item, _index, isFocused) => (
        <QuoteOption
          isFocused={!!isFocused}
          item={item}
          key={item.key}
          onSelect={handleSelectQuote}
        />
      )}
      searchText={searchText}
      trigger={trigger}
    />
  );
};

const useStyles = createStyles((theme) => ({
  dropdownContainer: {
    gap: theme.sk.spacing.spaceSmall,
    padding: theme.sk.spacing.spaceSmall,
    width: DROPDOWN_WIDTH,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.sk.spacing.spaceLarge,
  },
  newQuoteButton: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusMedium,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  optionHovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  quoteRow: {
    borderRadius: theme.sk.radii.radiusSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  trigger: {
    gap: theme.sk.spacing.spaceSmall,
    height: theme.sk.spacing.spaceXlarge,
    minHeight: theme.sk.spacing.spaceXlarge,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  updatedRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
